/**
 * 媒体 URL 解析与本校 OSS 预签名播放（缓存 + 批量 + 并发合并）。最后更新：2026-07-01
 */
import { BaseApiUrl } from '../api/base'
import { getMediaReadUrl, getMediaReadUrls, getOssVideoPlayUrl } from '../api/modules/misc';
import { getUploadSchoolId } from './ossMediaUpload'

/**
 * 缓存预签名播放地址：key = schoolId|inline|原始url
 * 存储位置：当前浏览器标签页内存（Map），刷新页面即清空；非 localStorage / 非 Service Worker。
 */
const playUrlCache = new Map()

/** 距过期不足该毫秒数时视为失效，提前重新签名 */
const CACHE_EXPIRE_BUFFER_MS = 60 * 1000

/**
 * 是否打印 OSS 媒体缓存日志（控制台执行 localStorage.setItem('ossMediaCacheDebug','1') 后刷新）
 */
function isOssMediaCacheDebug() {
  try {
    if (typeof window !== 'undefined' && window.__OSS_MEDIA_CACHE_DEBUG__ === true) return true
    return window.localStorage.getItem('ossMediaCacheDebug') === '1'
  } catch (e) {
    return false
  }
}

/** 调试日志（需先 ossMediaCache.debug(true)） */
export function ossMediaDebugLog(message, detail) {
  if (!isOssMediaCacheDebug()) return
  if (detail !== undefined) {
    console.log('[OSS媒体缓存]', message, detail)
  } else {
    console.log('[OSS媒体缓存]', message)
  }
}

function logOssMediaCache(message, detail) {
  ossMediaDebugLog(message, detail)
}

/**
 * 同一 key 并发请求合并为一次
 */
const playUrlInflight = new Map()

/**
 * 批量请求队列：nextTick 合并
 */
let batchQueue = []
let batchTimer = null

/**
 * 兼容后端 PascalCase / camelCase
 */
function pickField(obj, camelKey) {
  if (!obj) return undefined
  if (obj[camelKey] !== undefined && obj[camelKey] !== null) return obj[camelKey]
  const pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1)
  return obj[pascalKey]
}

/**
 * 是否为阿里云 OSS 对象 HTTPS 地址（需预签名播放）
 */
export function isAliyunOssHttpsUrl(path) {
  const p = String(path || '').trim()
  return /^https?:\/\//i.test(p) && /\.aliyuncs\.com\//i.test(p) && /\.oss-/i.test(p)
}

/**
 * 从 FileType 或 URL 取扩展名（含点），如 .pdf；FileType 为空时从路径推断
 */
export function fileExtFromMedia(path, fileType) {
  const fromType = String(fileType || '').trim().toLowerCase()
  if (fromType) return fromType.startsWith('.') ? fromType : '.' + fromType
  const clean = String(path || '').split('?')[0].split('#')[0]
  const dot = clean.lastIndexOf('.')
  if (dot < 0) return ''
  return clean.slice(dot).toLowerCase()
}

/**
 * 附件访问地址：OSS 走预签名，其它走 resolveMediaUrl
 */
export async function resolveAttachmentAccessUrl(path, schoolId, options) {
  const stable = stripOssUrlQuery(path)
  if (!stable) return ''
  if (isAliyunOssHttpsUrl(stable) && schoolId) {
    return resolveDisplayMediaUrl(stable, schoolId, options)
  }
  return resolveMediaUrl(stable)
}

/**
 * 解析媒体地址（同步）：
 * - blob: 原样
 * - 非本校 OSS 的 https: 原样
 * - 相对路径: BaseApiUrl + 路径
 * 注意：本校 OSS 的 https 请用 resolveDisplayMediaUrl / resolvePlayableMediaUrl。
 */
export function resolveMediaUrl(path) {
  if (!path) return ''
  const p = String(path).trim()
  if (p.startsWith('blob:')) return p
  if (/^https?:\/\//i.test(p)) return p
  const base = BaseApiUrl || ''
  if (!base) return p
  if (p.startsWith('/')) return base + p
  return base + '/' + p
}

/**
 * 解析后端返回的 Expiration 为毫秒时间戳
 */
function parseExpirationMs(expiration) {
  if (!expiration) return 0
  const t = Date.parse(expiration)
  return Number.isNaN(t) ? 0 : t
}

function buildCacheKey(schoolId, path, inline) {
  return String(schoolId) + '|' + (inline ? '1' : '0') + '|' + path
}

/** 读取内存中已缓存的 OSS 预签名 URL（供组件同步展示） */
export function lookupCachedOssSignedUrl(schoolId, stableUrl, inline) {
  if (!schoolId || !stableUrl) return null
  return getCached(buildCacheKey(schoolId, stableUrl, inline !== false), { silent: true })
}

function collectRowMediaValues(row, pickMediaField) {
  if (typeof pickMediaField === 'function') {
    const value = pickMediaField(row)
    if (value == null || value === '') return []
    return Array.isArray(value) ? value : [value]
  }
  if (Array.isArray(pickMediaField)) {
    const list = []
    pickMediaField.forEach((field) => {
      const value = row[field]
      if (value != null && value !== '') list.push(value)
    })
    return list
  }
  const single = row[pickMediaField]
  if (single == null || single === '') return []
  return [single]
}

function setCache(cacheKey, signedUrl, expiration) {
  const expiresAt = parseExpirationMs(expiration) || Date.now() + 3600 * 1000
  playUrlCache.set(cacheKey, { url: signedUrl, expiresAt })
  logOssMediaCache('写入', {
    key: cacheKey,
    expiresAt: new Date(expiresAt).toISOString(),
    ttlSec: Math.max(0, Math.round((expiresAt - Date.now()) / 1000)),
  })
}

function getCached(cacheKey, options) {
  const silent = !!(options && options.silent)
  const cached = playUrlCache.get(cacheKey)
  if (!cached) {
    if (!silent) logOssMediaCache('未命中', { key: cacheKey, reason: '无记录' })
    return null
  }
  const remainingMs = cached.expiresAt - Date.now()
  if (remainingMs > CACHE_EXPIRE_BUFFER_MS) {
    if (!silent) {
      logOssMediaCache('命中', {
        key: cacheKey,
        remainingSec: Math.round(remainingMs / 1000),
      })
    }
    return cached.url
  }
  if (!silent) {
    logOssMediaCache('未命中', {
      key: cacheKey,
      reason: remainingMs > 0 ? '临近过期' : '已过期',
      remainingSec: Math.round(remainingMs / 1000),
    })
  }
  return null
}

/**
 * 去掉 OSS 预签名查询参数，得到稳定对象地址（用于重新签名）
 */
export function stripOssUrlQuery(path) {
  const p = String(path || '').trim()
  if (!p || !/^https?:\/\//i.test(p)) return p
  try {
    const u = new URL(p)
    return u.origin + u.pathname
  } catch (e) {
    return p.split('?')[0]
  }
}

/**
 * 将 MediaFiles 等拼接字符串拆成路径数组（支持 , ; | 及 URL 编码分隔符 %3B 等）
 */
export function parseMediaFilesList(data) {
  if (!data) return []
  let raw = String(data).trim()
  if (!raw) return []
  if (/%3[bB]|%2[cC]|%7[cC]/.test(raw)) {
    try {
      raw = decodeURIComponent(raw)
    } catch (e) {
      /* 保持原串 */
    }
  }
  let parts = raw
    .split(/[,;|，；\n]+/)
    .map(s => s.trim())
    .filter(Boolean)
  if (parts.length === 1 && /https?:\/\//i.test(parts[0])) {
    const segments = parts[0].split(/(?=https?:\/\/)/i).map(s => s.trim()).filter(Boolean)
    if (segments.length > 1) parts = segments
  }
  return parts
}

/**
 * 展开 MediaJson 项：单条 url 内多路径（; 拼接）拆成多条。最后更新：2026-07-01
 */
export function expandMediaListItems(items) {
  const out = []
  let sort = 1
  for (const item of items || []) {
    const url = String((item && (item.url || item.Url)) || '').trim()
    if (!url) continue
    const type = (item && (item.type || item.Type)) || 'image'
    const cover = item && (item.cover || item.Cover)
    const parts = parseMediaFilesList(url)
    if (parts.length <= 1) {
      out.push({
        ...item,
        url: parts[0] || url,
        sort: item.sort != null ? item.sort : (item.Sort != null ? item.Sort : sort++),
      })
      continue
    }
    parts.forEach((p, i) => {
      const entry = { type, url: p, sort: sort++ }
      if (cover && i === 0) entry.cover = cover
      out.push(entry)
    })
  }
  return out.sort((a, b) => (a.sort || 0) - (b.sort || 0))
}

/**
 * 单条 OSS 预签名（优先 GetMediaReadUrl，失败回退 GetOssVideoPlayUrl 兼容旧路由）
 */
async function fetchSignedUrl(schoolId, stableUrl, inline) {
  const params = { schoolId: String(schoolId), url: stableUrl }
  if (inline) params.inline = true

  let res
  try {
    res = await getMediaReadUrl(params)
  } catch (e) {
    res = await getOssVideoPlayUrl(params)
  }

  const body = res.data
  const success = pickField(body, 'success') === true || (body && body.success === true)
  if (!body || !success) {
    throw new Error((body && (body.msg || body.Msg)) || '获取 OSS 读地址失败')
  }
  const resp = (body && body.response) || {}
  const signedUrl = pickField(resp, 'signedUrl')
  if (!signedUrl) {
    throw new Error('获取 OSS 读地址失败：未返回 SignedUrl')
  }
  return {
    signedUrl,
    expiration: pickField(resp, 'expiration'),
  }
}

/**
 * 批量预签名 OSS URL 并写入缓存
 */
export async function batchResolveOssMediaUrls(urls, schoolId, options) {
  const inline = !!(options && options.inline)
  if (!schoolId) return {}

  const stableList = []
  const keyMap = {}
  const result = {}
  let cacheHitCount = 0
  ;(urls || []).forEach((raw) => {
    const stable = stripOssUrlQuery(raw)
    if (!stable || !isAliyunOssHttpsUrl(stable)) return
    const cacheKey = buildCacheKey(schoolId, stable, inline)
    const cached = getCached(cacheKey, { silent: true })
    if (cached) {
      result[stable] = cached
      cacheHitCount++
      return
    }
    if (!stableList.includes(stable)) {
      stableList.push(stable)
      keyMap[stable] = cacheKey
    }
  })

  if (stableList.length === 0) {
    logOssMediaCache('批量', {
      schoolId: String(schoolId),
      inline,
      total: (urls || []).length,
      cacheHit: cacheHitCount,
      apiFetch: 0,
    })
    return result
  }

  logOssMediaCache('批量', {
    schoolId: String(schoolId),
    inline,
    total: (urls || []).length,
    cacheHit: cacheHitCount,
    apiFetch: stableList.length,
    fetchUrls: stableList,
  })

  const res = await getMediaReadUrls({
    schoolId: String(schoolId),
    urls: stableList,
    inline,
  })
  const body = res.data
  const success = pickField(body, 'success') === true
  if (!body || !success) {
    throw new Error((body && (body.msg || body.Msg)) || '批量获取 OSS 读地址失败')
  }

  const resp = body.response || body.Response || {}
  const items = resp.items || resp.Items || []
  items.forEach((item) => {
    const url = pickField(item, 'url') || item.Url
    const ok = pickField(item, 'success') === true || item.Success === true
    if (!url || !ok) return
    const signedUrl = pickField(item, 'signedUrl') || item.SignedUrl
    const expiration = pickField(item, 'expiration') || item.Expiration
    const stable = stripOssUrlQuery(url)
    const cacheKey = keyMap[stable] || buildCacheKey(schoolId, stable, inline)
    setCache(cacheKey, signedUrl, expiration)
    result[stable] = signedUrl
  })
  return result
}

/**
 * 列表加载后批量预取 OSS 缩略图（减少 N 次单条请求）
 */
export async function prefetchOssMediaFromRows(rows, pickMediaField, schoolId, options) {
  if (!rows || !rows.length || !schoolId) return
  const urls = []
  rows.forEach((row) => {
    collectRowMediaValues(row, pickMediaField).forEach((field) => {
      parseMediaFilesList(field).forEach((u) => {
        const stable = stripOssUrlQuery(u)
        if (isAliyunOssHttpsUrl(stable) && !urls.includes(stable)) urls.push(stable)
      })
    })
  })
  if (!urls.length) return
  const chunkSize = 80
  const chunks = Math.ceil(urls.length / chunkSize)
  logOssMediaCache('列表预取', {
    schoolId: String(schoolId),
    urlCount: urls.length,
    chunks,
  })
  for (let i = 0; i < urls.length; i += chunkSize) {
    await batchResolveOssMediaUrls(urls.slice(i, i + chunkSize), schoolId, options)
  }
}

/**
 * 列表页：根据当前页面上下文解析 schoolId 并预取 OSS 缩略图
 */
export async function prefetchTableOssMedia(vm, rows, pickMediaField, options) {
  const schoolId = getUploadSchoolId(vm)
  await prefetchOssMediaFromRows(rows, pickMediaField, schoolId, options)
}

/**
 * 展示用地址：非 OSS 同步拼接；OSS 走预签名（带缓存）
 */
export async function resolveDisplayMediaUrl(path, schoolId, options) {
  const inline = options && options.inline !== false
  const p = stripOssUrlQuery(path)
  if (!p) return ''
  if (p.startsWith('blob:') || p.startsWith('data:')) return p
  if (!/^https?:\/\//i.test(p)) {
    return resolveMediaUrl(p)
  }
  if (!isAliyunOssHttpsUrl(p)) {
    return p
  }
  if (!schoolId) {
    return p
  }

  const cacheKey = buildCacheKey(schoolId, p, inline)
  const hit = getCached(cacheKey)
  if (hit) return hit

  logOssMediaCache('单条请求接口', { schoolId: String(schoolId), url: p, inline })

  if (playUrlInflight.has(cacheKey)) {
    return playUrlInflight.get(cacheKey)
  }

  const task = (async () => {
    const { signedUrl, expiration } = await fetchSignedUrl(schoolId, p, inline)
    setCache(cacheKey, signedUrl, expiration)
    return signedUrl
  })()

  playUrlInflight.set(cacheKey, task)
  try {
    return await task
  } finally {
    if (playUrlInflight.get(cacheKey) === task) {
      playUrlInflight.delete(cacheKey)
    }
  }
}

/**
 * 可播放地址：本校 OSS HTTPS 走预签名，其它与 resolveMediaUrl 一致。
 */
export async function resolvePlayableMediaUrl(path, schoolId, options) {
  return resolveDisplayMediaUrl(path, schoolId, options)
}

/**
 * 清空预签名播放地址缓存（切换学校或调试时可调用）
 */
export function clearPlayableMediaUrlCache() {
  const size = playUrlCache.size
  playUrlCache.clear()
  playUrlInflight.clear()
  logOssMediaCache('已清空', { removed: size })
}

/**
 * 导出当前内存缓存条目（调试用）
 */
export function dumpOssMediaCache() {
  const now = Date.now()
  const rows = []
  playUrlCache.forEach((entry, key) => {
    rows.push({
      key,
      remainingSec: Math.round((entry.expiresAt - now) / 1000),
      expiresAt: new Date(entry.expiresAt).toISOString(),
      urlPreview: entry.url.length > 96 ? entry.url.slice(0, 96) + '…' : entry.url,
    })
  })
  if (typeof console !== 'undefined' && console.table) console.table(rows)
  return rows
}

/**
 * 开关缓存调试日志（无需改代码）
 * @param {boolean} enabled
 */
export function setOssMediaCacheDebug(enabled) {
  try {
    if (enabled) {
      window.localStorage.setItem('ossMediaCacheDebug', '1')
      window.__OSS_MEDIA_CACHE_DEBUG__ = true
    } else {
      window.localStorage.removeItem('ossMediaCacheDebug')
      window.__OSS_MEDIA_CACHE_DEBUG__ = false
    }
  } catch (e) {
    if (typeof window !== 'undefined') {
      window.__OSS_MEDIA_CACHE_DEBUG__ = !!enabled
    }
  }
  // 开关本身始终打印，便于确认命令已执行
  console.log('[OSS媒体缓存]', enabled ? '调试已开启' : '调试已关闭', '→ ossMediaCache.dump() 查看缓存')
}

/** 挂到 window，供 main.js 与控制台调试 */
export function installOssMediaCacheDebugTools() {
  if (typeof window === 'undefined') return
  window.ossMediaCache = {
    debug: setOssMediaCacheDebug,
    dump: dumpOssMediaCache,
    clear: clearPlayableMediaUrlCache,
  }
  try {
    if (window.localStorage.getItem('ossMediaCacheDebug') === '1') {
      window.__OSS_MEDIA_CACHE_DEBUG__ = true
      console.log('[OSS媒体缓存] 调试已开启（localStorage）→ 查询/翻页后看日志；ossMediaCache.dump() 查看缓存')
    }
  } catch (e) {
    /* ignore */
  }
}

/**
 * 拉取可播放地址并写入组件字段（OSS 才请求接口；带缓存与并发合并）
 */
export async function applyPlayableMediaUrl(vm, stateKey, path, schoolId) {
  const raw = String(path || '').trim()
  if (!raw) {
    vm[stateKey] = ''
    return ''
  }
  try {
    const url = await resolveDisplayMediaUrl(raw, schoolId)
    vm[stateKey] = url
    return url
  } catch (e) {
    const url = resolveMediaUrl(raw)
    vm[stateKey] = url
    console.warn('[applyPlayableMediaUrl]', e)
    return url
  }
}
